// hooks/academicPlan/useBulkOperations.ts
/**
 * Bulk operations hook
 * Manages clearing semesters, clearing all courses, and applying templates
 */
import { useCallback, useMemo } from 'react';
import { useToast } from '../useToast';
import { useConfirm } from '../../contexts/ConfirmContext';
import { dispatchDegreeAuditRefresh } from '../../utils/appEvents';
import { PlanActionType } from '../../adapters/plan';
import { typedApi } from '../../services/typedApiClient';
import { getErrorMessage } from '../../utils/errorUtils';
import type { AcademicPlan, SemesterCode, SemestersMap } from '../../types';
import type { CourseInput } from './useCourseOperations';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Parameters for the useBulkOperations hook
 */
export interface UseBulkOperationsParams {
  academicPlan: AcademicPlan;
  setAcademicPlan: React.Dispatch<React.SetStateAction<AcademicPlan>>;
  currentPlanId: number | null;
  savePlanToBackend: (planId: number, action: string, data: Record<string, unknown>) => Promise<unknown>;
  fetchAcademicPlan: () => Promise<void>;
  addCourse: (course: CourseInput, semester: SemesterCode) => Promise<boolean>;
}

/**
 * Return type for the useBulkOperations hook
 */
export interface ClearAllCoursesOptions {
  /** Also remove placeholder courses (#205). Default: keep them. */
  includePlaceholders?: boolean;
  /** Caller already showed its own confirmation dialog (CoursePlanner modal). */
  skipConfirm?: boolean;
}

export interface BulkOperations {
  clearSemester: (semester: SemesterCode) => Promise<void>;
  clearAllCourses: (options?: ClearAllCoursesOptions) => Promise<void>;
  applyTemplate: (distributionPlan: SemestersMap) => Promise<void>;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for bulk operations within an academic plan
 */
export const useBulkOperations = ({
  academicPlan,
  setAcademicPlan,
  currentPlanId,
  savePlanToBackend,
  fetchAcademicPlan,
  addCourse
}: UseBulkOperationsParams): BulkOperations => {
  const { showError, showSuccess } = useToast();
  const { confirm } = useConfirm();

  // Clear semester - remove from backend first, then update UI
  const clearSemester = useCallback(async (semester: SemesterCode): Promise<void> => {
    if (!currentPlanId || !academicPlan) return;

    const coursesToRemove = [...academicPlan.semesters[semester]];

    // Remove each course from backend FIRST, then update UI
    try {
      // Remove courses in reverse order to avoid index issues
      for (let i = coursesToRemove.length - 1; i >= 0; i--) {
        const course = coursesToRemove[i];
        if (!course.isPlaceholder) {
          await savePlanToBackend(currentPlanId, PlanActionType.RemoveCourse, {
            course_code: course.code,
            semester: semester
          });
        }
      }

      // Only update UI after all backend operations succeed
      setAcademicPlan(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          semesters: {
            ...prev.semesters,
            [semester]: prev.semesters[semester].filter(c => c.isPlaceholder)
          }
        };
      });
      showSuccess(`Cleared all courses from semester`);

      // Trigger degree audit refresh after clearing semester
      dispatchDegreeAuditRefresh(currentPlanId);
    } catch (err) {
      showError(`Failed to clear semester: ${getErrorMessage(err)}`);
    }
  }, [currentPlanId, academicPlan, setAcademicPlan, savePlanToBackend, showError, showSuccess]);

  // Clear all courses via the single backend command (POST /clear) — one
  // request instead of a per-course loop from the browser, matching how the
  // plan-switcher clear and the chat command already work.
  const clearAllCourses = useCallback(async (options?: ClearAllCoursesOptions): Promise<void> => {
    const includePlaceholders = options?.includePlaceholders ?? false;
    if (!options?.skipConfirm) {
      const confirmed = await confirm({
        title: 'Clear All Courses',
        message: 'Clear all courses from your plan? This cannot be undone.',
        confirmText: 'Clear All',
        variant: 'danger'
      });
      if (!confirmed) return;
    }

    if (!academicPlan || !currentPlanId) return;

    try {
      // Optimistic: clear the UI first. Placeholders survive unless opted in
      // (#205 — a placeholder encodes intent, not a chosen course).
      setAcademicPlan(prev => {
        if (!prev) return prev;
        const newSemesters: SemestersMap = {};
        Object.entries(prev.semesters).forEach(([semester, courses]) => {
          newSemesters[semester] = includePlaceholders
            ? []
            : courses.filter(c => c.isPlaceholder);
        });

        return {
          ...prev,
          semesters: newSemesters
        };
      });

      await typedApi.academicPlans.clear(currentPlanId, { includePlaceholders });
      showSuccess('All courses have been cleared from your plan.');

      // Trigger degree audit refresh after clearing all courses
      dispatchDegreeAuditRefresh(currentPlanId);
    } catch (err) {
      showError(`Failed to clear all courses: ${getErrorMessage(err)}`);
      // Refresh to ensure UI matches backend state
      await fetchAcademicPlan();
    }
  }, [academicPlan, setAcademicPlan, currentPlanId, fetchAcademicPlan, showError, showSuccess, confirm]);

  // Apply a template to the plan
  const applyTemplate = useCallback(async (distributionPlan: SemestersMap): Promise<void> => {
    if (!currentPlanId) return;

    // Clear existing courses first
    await clearAllCourses();

    // Add courses from template
    for (const [semester, courses] of Object.entries(distributionPlan)) {
      for (const course of courses) {
        await addCourse(course as CourseInput, semester);
      }
    }
  }, [currentPlanId, clearAllCourses, addCourse]);

  // Memoize operations object
  return useMemo(() => ({
    clearSemester,
    clearAllCourses,
    applyTemplate
  }), [clearSemester, clearAllCourses, applyTemplate]);
};
